/**
 * Shared instrument stage: preloads the samples, mounts the instrument
 * layout, and cuts the voices on exit. The free-instrument route, song
 * player, and mini-games (M3) all reuse it.
 */

import { createResource, Match, onCleanup, Show, Suspense, Switch } from "solid-js";
import { Loader2 } from "@fleet/icons";
import type { InstrumentId, SoundKey } from "@melodimals/game-core";
import { useAudio } from "@/providers/audio-provider";
import { PianoKeyboard } from "@/features/instruments/piano-keyboard";
import { XylophoneBars } from "@/features/instruments/xylophone-bars";
import { DrumKit } from "@/features/instruments/drum-kit";

interface InstrumentStageProps {
  instrument: InstrumentId;
  highlightedKey?: SoundKey | null;
  onNotePlayed?: (key: SoundKey) => void;
  class?: string;
}

export function InstrumentStage(props: InstrumentStageProps) {
  const audio = useAudio();
  const [ready] = createResource(
    () => props.instrument,
    async (instrument) => {
      await audio.loadInstrument(instrument);
      return true;
    },
  );

  onCleanup(() => {
    audio.allNotesOff();
  });

  return (
    <Suspense fallback={<StageLoader />}>
      <Show when={ready()} fallback={<StageLoader />}>
        <Switch>
          <Match when={props.instrument === "piano"}>
            <PianoKeyboard
              highlightedKey={props.highlightedKey}
              onNotePlayed={props.onNotePlayed}
              class={props.class}
            />
          </Match>
          <Match when={props.instrument === "xylophone"}>
            <XylophoneBars
              highlightedKey={props.highlightedKey}
              onNotePlayed={props.onNotePlayed}
              class={props.class}
            />
          </Match>
          <Match when={props.instrument === "drums"}>
            <DrumKit
              highlightedKey={props.highlightedKey}
              onNotePlayed={props.onNotePlayed}
              class={props.class}
            />
          </Match>
        </Switch>
      </Show>
    </Suspense>
  );
}

function StageLoader() {
  return (
    <div class="flex h-full items-center justify-center">
      <Loader2 class="h-12 w-12 animate-spin text-white/70" />
    </div>
  );
}
